import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";
import HubSpotAccounts from "./HubSpotAccounts";
import IntegrationActivityLog from "../IntegrationActivityLog";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import LaunchWizardButton from "@/components/LaunchWizardButton";
import { IconClose1 } from "@velaro/velaro-component-lib";

interface HubSpotConfig {
  id: number;
  siteId: number;
  displayName: string;
  hubId: string;
  portalName: string;
  pushTranscripts: boolean;
  createContactIfMissing: boolean;
  enabled: boolean;
  createdAt: string;
  accessTokenMasked: string;
}

const CAPABILITIES = [
  {
    title: "Contact lookup & sync",
    icon: "👤",
    items: [
      "Find any HubSpot contact instantly by email address — name, company, lifecycle stage, and open deals returned in seconds",
      "Retrieve the full contact record including custom properties, associated deals, and complete activity timeline",
      "Automatically create a new HubSpot contact when a visitor's email isn't found (configurable per connection)",
      "Push chat transcripts as timestamped Notes directly on the matched contact record"
    ]
  },
  {
    title: "Automated transcript push",
    icon: "📝",
    items: [
      "When a conversation ends, the full transcript is automatically pushed to HubSpot — no agent action required",
      "Attached as a Note on the matched contact so your sales team has complete chat context",
      "Configurable per connected HubSpot account (toggle in the Accounts tab)"
    ]
  },
  {
    title: "AI workflow tools (29)",
    icon: "🤖",
    items: [
      "29 AI tools available in every workflow and bot node: contacts (5), companies (3), deals & pipeline (7 including quotes), tickets (5), tasks (2), engagement & team (7 including email sequences, form submissions, and meetings)",
      "Create HubSpot quotes and send shareable checkout links directly in chat — close sales without leaving the conversation",
      "Enroll contacts into email sequences mid-chat for automated post-conversation follow-up",
      "No workflow configuration required — the AI decides when to call HubSpot based on conversation context",
      "Multiple HubSpot accounts supported — connect portals for different brands or regions on the same site"
    ]
  }
];

const AI_TOOLS = [
  // Contact Management (5)
  {
    name: "hubspot_search_contact",
    label: "Search contact",
    group: "Contact Management",
    description: "Find a HubSpot contact by email or phone. Returns name, company, lifecycle stage, and deal info."
  },
  {
    name: "hubspot_get_contact",
    label: "Get contact details",
    group: "Contact Management",
    description: "Fetch full contact record including custom properties, associated deals, and activity history."
  },
  {
    name: "hubspot_update_contact",
    label: "Update contact",
    group: "Contact Management",
    description: "Update contact properties such as lifecycle stage, job title, phone, or owner."
  },
  {
    name: "hubspot_create_contact",
    label: "Create contact",
    group: "Contact Management",
    description: "Create a new HubSpot contact from visitor data collected during the conversation."
  },
  {
    name: "hubspot_search_contacts",
    label: "Advanced contact search",
    group: "Contact Management",
    description: "Multi-filter search across contacts — by name, company, owner, lifecycle stage, or date."
  },
  // Company (3)
  {
    name: "hubspot_search_companies",
    label: "Search companies",
    group: "Company",
    description: "Find companies in HubSpot by name or domain. Returns industry, size, and associated contacts."
  },
  {
    name: "hubspot_get_company",
    label: "Get company details",
    group: "Company",
    description: "Retrieve full company record including all properties, associated contacts, and linked deals."
  },
  {
    name: "hubspot_get_contact_companies",
    label: "Get contact's companies",
    group: "Company",
    description: "List all companies associated with a contact — useful for multi-company contacts."
  },
  // Deals & Pipeline (5)
  {
    name: "hubspot_get_deals",
    label: "Get contact's deals",
    group: "Deals & Pipeline",
    description: "List all deals associated with a contact — shows stage, amount, and close date."
  },
  {
    name: "hubspot_search_deals",
    label: "Search deals",
    group: "Deals & Pipeline",
    description: "Find deals by name, stage, pipeline, or associated contact."
  },
  {
    name: "hubspot_get_deal",
    label: "Get deal details",
    group: "Deals & Pipeline",
    description: "Retrieve full deal record: amount, pipeline stage, close date, and associated contacts."
  },
  {
    name: "hubspot_update_deal",
    label: "Update deal",
    group: "Deals & Pipeline",
    description: "Update a deal's stage, amount, close date, or owner from conversation data."
  },
  {
    name: "hubspot_create_deal",
    label: "Create deal",
    group: "Deals & Pipeline",
    description: "Create a new deal in HubSpot and associate it with a contact and company."
  },
  // Tickets & Support (5)
  {
    name: "hubspot_create_ticket",
    label: "Create ticket",
    group: "Tickets & Support",
    description: "Create a HubSpot Service Hub support ticket linked to the contact and conversation."
  },
  {
    name: "hubspot_get_ticket",
    label: "Get ticket details",
    group: "Tickets & Support",
    description: "Retrieve a support ticket by ID — status, priority, pipeline stage, and description."
  },
  {
    name: "hubspot_get_contact_tickets",
    label: "Get contact's tickets",
    group: "Tickets & Support",
    description: "List all support tickets associated with a contact — useful for repeat-issue detection."
  },
  {
    name: "hubspot_search_tickets",
    label: "Search tickets",
    group: "Tickets & Support",
    description: "Find tickets by subject, status, priority, or pipeline stage."
  },
  {
    name: "hubspot_update_ticket",
    label: "Update ticket",
    group: "Tickets & Support",
    description: "Update a ticket's status, priority, owner, or pipeline stage from the conversation."
  },
  // Tasks (2)
  {
    name: "hubspot_get_contact_tasks",
    label: "Get contact's tasks",
    group: "Tasks",
    description: "Retrieve open and completed tasks associated with a contact for follow-up context."
  },
  {
    name: "hubspot_create_task",
    label: "Create task",
    group: "Tasks",
    description: "Create a follow-up task in HubSpot — assigned to an owner with due date and subject."
  },
  // Engagement & Team (4)
  {
    name: "hubspot_add_note",
    label: "Add note to contact",
    group: "Engagement & Team",
    description: "Attach a note (e.g. conversation summary or transcript) to a contact's activity timeline."
  },
  {
    name: "hubspot_get_contact_timeline",
    label: "Get contact timeline",
    group: "Engagement & Team",
    description: "Retrieve recent engagements on a contact — emails, calls, meetings, and notes."
  },
  {
    name: "hubspot_get_owners",
    label: "Get HubSpot owners",
    group: "Engagement & Team",
    description: "List HubSpot users (owners/reps) — use to assign contacts, deals, or tickets to the right person."
  },
  {
    name: "hubspot_create_engagement",
    label: "Log call or meeting",
    group: "Engagement & Team",
    description: "Create an engagement record (call or meeting) on a contact to log conversation activity."
  },
  {
    name: "hubspot_get_pipeline_stages",
    label: "Get pipeline stages",
    group: "Deals & Pipeline",
    description: "Returns all deal and ticket pipeline stages with their IDs and labels. Use this before updating deal/ticket stages to get the correct stage ID for your pipeline."
  },
  {
    name: "hubspot_create_quote",
    label: "Create quote",
    group: "Deals & Pipeline",
    description: "Create a HubSpot quote associated with a deal. Returns a shareable quote link that the AI can send directly in chat — close sales without leaving the conversation."
  },
  {
    name: "hubspot_enroll_sequence",
    label: "Enroll in sequence",
    group: "Engagement & Team",
    description: "Enroll a HubSpot contact in an email sequence for automated follow-up. Use to trigger nurture campaigns or post-chat follow-ups automatically from the conversation."
  },
  {
    name: "hubspot_get_form_submissions",
    label: "Get form submissions",
    group: "Contact Management",
    description: "Retrieve HubSpot form submission records for a contact. Lets the AI see what a visitor has previously submitted — pricing inquiries, demo requests, support tickets."
  },
  {
    name: "hubspot_get_meetings",
    label: "Get meetings",
    group: "Engagement & Team",
    description: "List meetings and calls associated with a HubSpot contact. Shows upcoming and past meeting history so the AI can reference prior touchpoints during the conversation."
  }
];

const AI_TOOL_GROUPS = ["Contact Management", "Company", "Deals & Pipeline", "Tickets & Support", "Tasks", "Engagement & Team"];

// ─── Field Reference ───────────────────────────────────────────────────────────

const HS_FIELD_REFERENCE = [
  {
    scope: "Contact",
    scopeKey: "contact",
    fields: [
      { field: "email", label: "Email", notes: "Primary identifier" },
      { field: "firstname", label: "First Name", notes: "" },
      { field: "lastname", label: "Last Name", notes: "" },
      { field: "company", label: "Company", notes: "" },
      { field: "phone", label: "Phone", notes: "" },
      { field: "mobilephone", label: "Mobile Phone", notes: "" },
      { field: "jobtitle", label: "Job Title", notes: "" },
      { field: "lifecyclestage", label: "Lifecycle Stage", notes: "lead / subscriber / marketingqualifiedlead / salesqualifiedlead / opportunity / customer" },
      { field: "hs_lead_status", label: "Lead Status", notes: "NEW / OPEN / IN_PROGRESS / OPEN_DEAL / UNQUALIFIED / ATTEMPTED_TO_CONTACT / CONNECTED / BAD_TIMING" },
      { field: "hubspot_owner_id", label: "Owner (User ID)", notes: "Numeric HubSpot user ID" },
      { field: "notes_last_contacted", label: "Last Contacted", notes: "ISO datetime" },
      { field: "hs_sales_email_last_replied", label: "Last Email Reply", notes: "ISO datetime" },
      { field: "createdate", label: "Created Date", notes: "ISO datetime" }
    ]
  },
  {
    scope: "Company",
    scopeKey: "company",
    fields: [
      { field: "name", label: "Company Name", notes: "" },
      { field: "domain", label: "Domain", notes: "e.g. acme.com" },
      { field: "industry", label: "Industry", notes: "" },
      { field: "annualrevenue", label: "Annual Revenue", notes: "Numeric" },
      { field: "numberofemployees", label: "Employees", notes: "Numeric" },
      { field: "city", label: "City", notes: "" },
      { field: "country", label: "Country", notes: "" },
      { field: "phone", label: "Phone", notes: "" },
      { field: "hubspot_owner_id", label: "Owner (User ID)", notes: "Numeric HubSpot user ID" }
    ]
  },
  {
    scope: "Deal",
    scopeKey: "deal",
    fields: [
      { field: "dealname", label: "Deal Name", notes: "" },
      { field: "dealstage", label: "Stage (ID)", notes: "Pipeline stage ID — use Get pipeline stages tool to resolve labels" },
      { field: "pipeline", label: "Pipeline (ID)", notes: "Pipeline ID" },
      { field: "amount", label: "Amount", notes: "Numeric, no currency symbol" },
      { field: "closedate", label: "Close Date", notes: "ISO datetime" },
      { field: "hs_deal_stage_probability", label: "Win Probability", notes: "0.0 – 1.0" },
      { field: "hubspot_owner_id", label: "Owner (User ID)", notes: "Numeric HubSpot user ID" },
      { field: "hs_is_closed_won", label: "Closed Won", notes: "Boolean" },
      { field: "createdate", label: "Created Date", notes: "ISO datetime" }
    ]
  },
  {
    scope: "Ticket",
    scopeKey: "ticket",
    fields: [
      { field: "subject", label: "Subject", notes: "" },
      { field: "content", label: "Description", notes: "" },
      { field: "hs_pipeline_stage", label: "Pipeline Stage (ID)", notes: "" },
      { field: "hs_ticket_priority", label: "Priority", notes: "LOW / MEDIUM / HIGH / URGENT" },
      { field: "hs_resolution", label: "Resolution", notes: "" },
      { field: "createdate", label: "Created Date", notes: "ISO datetime" },
      { field: "hubspot_owner_id", label: "Owner (User ID)", notes: "Numeric HubSpot user ID" }
    ]
  }
];

function HsCopyVariableButton({ scopeKey, field }: { scopeKey: string; field: string }) {
  const [copied, setCopied] = React.useState(false);
  const variable = `{hubspot.${scopeKey}.${field}}`;
  async function copy() {
    try { await navigator.clipboard.writeText(variable); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* */ }
  }
  return (
    <button
      type="button"
      onClick={copy}
      title={`Copy: ${variable}`}
      aria-label={`Copy variable ${variable}`}
      className={`text-[9px] px-1.5 py-0.5 rounded font-mono border transition-colors ${
        copied ? "border-green-300 bg-green-50 text-green-700" : "border-slate-200 bg-slate-50 text-slate-400 hover:border-orange-300 hover:text-orange-600 hover:bg-orange-50"
      }`}
    >
      {copied ? "✓ copied" : variable}
    </button>
  );
}

function HubSpotFieldReference() {
  const [activeScope, setActiveScope] = React.useState("Contact");
  const scopeData = HS_FIELD_REFERENCE.find((s) => s.scope === activeScope) ?? HS_FIELD_REFERENCE[0];

  return (
    <div className="bg-white rounded-2xl shadow-md p-6">
      <div className="font-semibold text-slate-800 text-lg mb-1">Field Reference</div>
      <p className="text-sm text-slate-500 mb-4">
        Standard HubSpot field names and the variable syntax to use in workflow HTTP POST nodes.
        The agent CRM panel shows contact, company, deal, and ticket data automatically — no configuration needed.
      </p>

      {/* What you get callout */}
      <div className="rounded-lg bg-orange-50 border border-orange-200 p-4 mb-5">
        <div className="grid grid-cols-1 gap-2 text-xs">
          <div className="flex items-start gap-2 text-orange-800">
            <span className="text-base leading-none">✅</span>
            <span><strong>Agent sidebar</strong> — automatically shows the matched contact, their open deals, tickets, and company during every live chat. No mapping config needed.</span>
          </div>
          <div className="flex items-start gap-2 text-orange-800">
            <span className="text-base leading-none">📋</span>
            <span><strong>HTTP POST variables</strong> — copy any variable below and paste into a workflow HTTP POST node to forward HubSpot data to another system.</span>
          </div>
        </div>
      </div>

      {/* Scope selector */}
      <div className="flex gap-2 mb-4">
        {HS_FIELD_REFERENCE.map((s) => (
          <button
            key={s.scope}
            type="button"
            onClick={() => setActiveScope(s.scope)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              activeScope === s.scope ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {s.scope}
          </button>
        ))}
      </div>

      {/* Example */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 mb-4 text-xs text-slate-600">
        <div className="flex items-center justify-between mb-1.5">
          <strong className="text-slate-700">Using these in a workflow HTTP POST node</strong>
          <span className="text-slate-400">Click any variable in the table to copy</span>
        </div>
        <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 text-xs font-mono overflow-x-auto whitespace-pre leading-relaxed mt-2">{`{
  "contact_email":   "{hubspot.contact.email}",
  "contact_name":    "{hubspot.contact.firstname} {hubspot.contact.lastname}",
  "lifecycle_stage": "{hubspot.contact.lifecyclestage}",
  "deal_name":       "{hubspot.deal.dealname}",
  "deal_value":      "{hubspot.deal.amount}",
  "ticket_subject":  "{hubspot.ticket.subject}"
}`}</pre>
      </div>

      {/* Field table */}
      <div className="rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-2.5 bg-slate-800 text-white text-xs font-semibold">
          {scopeData.scope} fields
        </div>
        <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">HubSpot property</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Friendly name</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Variable to copy</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {scopeData.fields.map((f, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                  <td className="px-3 py-2 font-mono text-[10px] text-slate-700">{f.field}</td>
                  <td className="px-3 py-2 text-slate-700">{f.label}</td>
                  <td className="px-3 py-2">
                    <HsCopyVariableButton scopeKey={scopeData.scopeKey} field={f.field} />
                  </td>
                  <td className="px-3 py-2 text-slate-400 italic text-[10px]">{f.notes || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs text-slate-400 mt-3">
        These are standard HubSpot CRM properties available on every portal. Custom properties use the same variable syntax — replace the field name with your custom property internal name.
      </p>
    </div>
  );
}

function CapabilitiesPanel() {
  const [open, setOpen] = useState(false);
  const [openSections, setOpenSections] = useState<Set<number>>(new Set([0]));

  function toggleSection(i: number) {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  }

  return (
    <div className="bg-white rounded-2xl shadow-md mb-4">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-slate-50 rounded-2xl"
      >
        <div className="flex items-center gap-3">
          <span className="font-semibold text-slate-800">What's included</span>
          <span className="text-xs bg-green-100 text-green-700 font-medium px-2 py-0.5 rounded-full">
            {CAPABILITIES.length} features
          </span>
        </div>
        <svg
          className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`}
          viewBox="0 0 16 16" fill="currentColor"
        >
          <path d="M8 10.5L3 5.5H13L8 10.5Z" />
        </svg>
      </button>

      {open && (
        <div className="border-t border-slate-100 px-6 pb-6">
          <div className="space-y-2 mt-4">
            {CAPABILITIES.map((cap, i) => (
              <div key={i} className="rounded-lg border border-slate-100 overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection(i)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-base">{cap.icon}</span>
                    <span className="text-sm font-medium text-slate-700">{cap.title}</span>
                  </div>
                  <svg
                    className={`w-3.5 h-3.5 text-slate-400 transition-transform flex-shrink-0 ${openSections.has(i) ? "rotate-180" : ""}`}
                    viewBox="0 0 16 16" fill="currentColor"
                  >
                    <path d="M8 10.5L3 5.5H13L8 10.5Z" />
                  </svg>
                </button>
                {openSections.has(i) && (
                  <div className="px-4 pb-3 space-y-1.5 border-t border-slate-50">
                    {cap.items.map((item, j) => (
                      <div key={j} className="flex items-start gap-2 pt-1.5">
                        <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                        <span className="text-xs text-slate-600">{item}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

        </div>
      )}
    </div>
  );
}

export default function HubSpot() {
  const api = useApi();
  const navigate = useNavigate();
  const [configs, setConfigs] = useState<HubSpotConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"setup" | "field-reference" | "logs">("setup");
  const [showWizard, setShowWizard] = useState(false);

  const fetchConfigs = useCallback(async () => {
    try {
      const resp = await api.admin.get("HubSpot/Configs");
      const data = await resp.json();
      setConfigs(Array.isArray(data) ? data : []);
    } catch {
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  const hasActiveConfig = configs.some((c) => c.enabled);

  return (
    <div>
      {showWizard && (
        <StarterWorkflowWizard
          integration="hubspot"
          hubspotConfigId={configs.find((c) => c.enabled)?.id}
          onClose={() => setShowWizard(false)}
        />
      )}

      {/* Tabs */}
      <div className="flex border-b border-slate-200 mb-4">
        {(["setup", "field-reference", "logs"] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            className={`py-2 mr-4 text-sm cursor-pointer ${activeTab === tab ? "border-b-2 border-cornflower-blue-500 text-cornflower-blue-600 font-medium" : "text-slate-500"}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === "setup" ? "Setup" : tab === "field-reference" ? "Field Reference" : "Activity Log"}
          </button>
        ))}
      </div>

      {hasActiveConfig && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex flex-col gap-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-blue-800 mb-0.5">
                Deploy the chat widget to your site
              </div>
              <div className="text-xs text-blue-600">
                Your HubSpot integration is connected — grab your embed code from Deployments to go live.
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <a href="/chat-windows" className="text-xs px-3 py-1.5 rounded-lg bg-white border border-blue-300 text-blue-700 hover:bg-blue-100 transition">Chat Window</a>
              <a href="/deployments" className="text-xs px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition">Get Embed Code →</a>
            </div>
          </div>
          <div className="flex items-start gap-2 bg-white/60 rounded-lg px-3 py-2 text-xs text-blue-700 border border-blue-100">
            <i className="fa-solid fa-lightbulb mt-0.5 shrink-0" />
            <span>
              <strong>Tip:</strong> You can also create a <strong>Knowledge Base Q&A</strong> page widget
              from <a href="/deployments" className="underline hover:text-blue-900">Deployments → Page Widgets</a> for
              a lightweight, AI-only Q&A experience — no live agent needed.
            </span>
          </div>
        </div>
      )}

      {activeTab === "field-reference" && (
        <HubSpotFieldReference />
      )}

      {activeTab === "logs" && (
        <div className="bg-white rounded-2xl shadow-md p-6">
          <IntegrationActivityLog integrationSlug="HubSpot" />
        </div>
      )}

      {activeTab === "setup" && (
        <div>
          {/* Capabilities — always visible */}
          <CapabilitiesPanel />

          {loading ? (
            <div className="flex justify-center py-12"><LoadingSpinner /></div>
          ) : (
            <>
              <div className="mb-6">
                <HubSpotAccounts
                  configs={configs}
                  onDelete={(id) => setConfigs((prev) => prev.filter((c) => c.id !== id))}
                  onRefresh={fetchConfigs}
                />
              </div>

              {/* AI Tools panel */}
              {configs.length > 0 && (
                <div className="bg-white rounded-2xl shadow-md p-6 mb-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="font-semibold text-slate-800 text-lg flex items-center gap-2">
                        AI Tools
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${hasActiveConfig ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                          {hasActiveConfig ? "Active" : "No active connection"}
                        </span>
                      </div>
                      <div className="text-sm text-slate-500 mt-0.5">
                        29 tools available to any AI workflow node on this site. No configuration required — the AI decides when to call HubSpot based on conversation context.
                      </div>
                    </div>
                    {hasActiveConfig && (
                      <LaunchWizardButton onClick={() => setShowWizard(true)} />
                    )}
                  </div>

                  <div className="border-t border-slate-100 pt-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
                        Available AI tools
                      </div>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">29 tools</span>
                    </div>
                    {AI_TOOL_GROUPS.map((group) => (
                      <div key={group} className="mb-4">
                        <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-2">{group}</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {AI_TOOLS.filter((t) => t.group === group).map((tool) => (
                            <div
                              key={tool.name}
                              className={`flex gap-3 p-3 rounded-lg border ${hasActiveConfig ? "border-green-200 bg-green-50" : "border-slate-200 bg-slate-50"}`}
                            >
                              <div className={`mt-0.5 w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center ${hasActiveConfig ? "bg-green-500" : "bg-slate-300"}`}>
                                {hasActiveConfig && (
                                  <svg viewBox="0 0 10 10" width="8" height="8" fill="none">
                                    <path d="M1.5 5L3.5 7L8.5 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                )}
                              </div>
                              <div>
                                <div className="text-sm font-medium text-slate-700">{tool.label}</div>
                                <div className="text-xs text-slate-500 mt-0.5">{tool.description}</div>
                                <code className="text-[10px] text-slate-400 mt-1 block">{tool.name}</code>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Transcript push status */}
              {configs.some((c) => c.pushTranscripts && c.enabled) && (
                <div className="bg-white rounded-2xl shadow-md p-6">
                  <div className="font-semibold text-slate-800 text-lg mb-2">Transcript Push</div>
                  <div className="text-sm text-slate-500 mb-3">
                    Enabled on {configs.filter((c) => c.pushTranscripts && c.enabled).length} connection(s). Chat transcripts are automatically pushed to HubSpot as Notes when conversations end.
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
                    ✓ Auto-push active — transcripts will appear on the matched HubSpot contact after each chat.
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function BackToIntegrationLandingHubSpot() {
  const navigate = useNavigate();
  return (
    <div className="flex cursor-pointer" onClick={() => navigate("/integrations")}>
      <IconClose1 className="h-6 w-6" />
    </div>
  );
}
